/* ==========================================================================
   Mega menu behaviour
   One custom element. Custom elements survive theme editor re-render, which
   jQuery $(document).ready() does not — that is why the old menu broke while
   editing. Do not move this logic back into a snippet.

   CLICK TO OPEN — Ben's decision, 17-09-2026 (#48, built as #157).
   The menu opens on click only. The top level label of a panel is a BUTTON,
   not a link, so the thing you press and the thing that happens agree.

   What was deliberately REMOVED, so nobody puts it back thinking it was lost:
     - mouseenter on a trigger. There is no hover opening at any width.
     - focus on a trigger auto-opening the panel. Tabbing along the menu bar
       now moves through the labels without unfurling a panel over the page.
     - mouseleave on the menu closing it, and the whole MM_TIMING table of
       intent delays. Those delays existed ONLY to make hover survivable —
       a cursor travelling into an open panel crosses the next trigger on the
       way, and 260ms was how long it was given to do that. With click there
       is nothing to protect against, so a delay would just be lag.

   What replaced them: a click toggles, Escape closes and returns focus to the
   trigger, a click anywhere outside closes, and focus leaving the menu closes.
   ========================================================================== */

class MegaMenu extends HTMLElement {
  connectedCallback() {
    this.triggers = [...this.querySelectorAll('[data-mm-trigger]')];
    this.panels = [...this.querySelectorAll('[data-mm-panel]')];
    this.current = null;

    this.triggers.forEach((trigger) => {
      const id = trigger.dataset.mmTrigger;

      /* The trigger is a <button>, so Enter and Space already arrive here as
         a click. A separate keydown handler calling toggle() would fire on
         the keydown AND on the click it produces — two toggles, which lands
         back where it started and reads as a dead key. That is why there is
         no keydown handler on the trigger. */
      trigger.addEventListener('click', () => {
        if (this.isDrawer) {
          this.dataset.level = 'panel';
          this.open(id);
          const title = this.querySelector('.mm-drawer-chrome__title');
          if (title) {
            const triggerTitle = trigger.querySelector('.mm-link__title') || trigger.querySelector('.mm-nav-desktop');
            if (triggerTitle) {
              title.textContent = triggerTitle.textContent.trim();
            }
          }
          return;
        }
        this.toggle(id);
      });
    });

    this.panels.forEach((panel) => this.initSteps(panel));
    this.initMarker();
    this.initFrontChat();
    this.probeSearch();
    this.initCall();

    this.overlay = document.querySelector('[data-mm-overlay]');
    this.dataset.drawer = 'closed';
    this.dataset.level = 'root';

    /*  ── NO SLIDE WHEN THE WINDOW CROSSES THE HAMBURGER WIDTH — dev-build 214 ──
        At 1279px and below, mega-menu.css turns this whole element into the drawer:
        position fixed, parked off screen with translateX(100%), and a 0.24s
        transition on transform so that opening and closing slide. Resizing across
        1280px changes the transform too, so the browser animated the menu from
        where it sat on desktop out to its parked spot — the flash of a half-open
        drawer on the right (Ben, 26-09-2026). The slide is only meant for opening
        and closing, so it is switched off for the two frames either side of the
        crossing. The media-query change event fires before the browser works out
        the new styles for that frame, so the class is already there when the
        transform changes.

        Leaving drawer mode with the drawer open also closes it, so the overlay
        is not left over a desktop page.                                        */
    this.drawerQuery = window.matchMedia('(max-width: 1279px)');
    this.onDrawerQuery = () => {
      const quiet = [this, this.overlay].filter(Boolean);
      quiet.forEach((el) => el.classList.add('mm-no-anim'));
      if (!this.drawerQuery.matches && this.dataset.drawer === 'open') this.closeDrawer();
      void this.offsetWidth;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        quiet.forEach((el) => el.classList.remove('mm-no-anim'));
      }));
    };
    if (this.drawerQuery.addEventListener) this.drawerQuery.addEventListener('change', this.onDrawerQuery);
    else if (this.drawerQuery.addListener) this.drawerQuery.addListener(this.onDrawerQuery);

    document.querySelectorAll('[data-mm-drawer-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.openDrawer(btn));
    });
    this.querySelectorAll('[data-mm-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeDrawer());
    });
    this.querySelectorAll('[data-mm-back]').forEach((btn) => {
      btn.addEventListener('click', () => this.back());
    });
    if (this.overlay) this.overlay.addEventListener('click', () => this.closeDrawer());

    /* Click outside closes. Held as a named handler because the theme editor
       re-renders the section and disconnectedCallback has to take it off the
       document again — an anonymous listener here would accumulate one copy
       per edit. */
    this.onDocumentClick = (e) => {
      if (this.isDrawer || !this.current) return;
      if (!this.contains(e.target)) this.close();
    };
    document.addEventListener('click', this.onDocumentClick);

    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.current) return;
      if (this.isDrawer) { this.closeDrawer(); return; }
      const trigger = this.triggers.find((t) => t.dataset.mmTrigger === this.current);
      this.close();
      if (trigger) trigger.focus();   // focus must go back where it came from
    });

    /* Focus leaving the menu entirely closes it (tabbing past the last link).

       THE relatedTarget GUARD IS THE WHOLE FIX — reported 18-09-2026: clicking
       blank space anywhere on the menu surface closed the panel.

       Diagnosis, because the obvious suspect was innocent: the click-outside
       handler was fine, since the panel is inside this element and contains()
       returned true. It was THIS handler. Pressing blank space blurs the
       trigger, and a blur that lands on nothing focusable reports
       relatedTarget as null. null is not contained by anything, so the test
       read "focus left the menu" and closed it — on a click that never left
       the menu at all.

       So a null relatedTarget now means "focus went nowhere", which is not the
       same as "focus went outside" and must not be treated as it. Tabbing past
       the last link still reports a real element and still closes. */
    this.addEventListener('focusout', (e) => {
      if (this.isDrawer) return;
      if (!e.relatedTarget) return;            // focus went nowhere, not outside
      if (!this.contains(e.relatedTarget)) this.close();
    });
  }

  disconnectedCallback() {
    if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
    if (this.onMarkerResize) window.removeEventListener('resize', this.onMarkerResize);
    if (this.onBookingChange) document.removeEventListener('bookingbar:changed', this.onBookingChange);
    if (this.drawerQuery && this.onDrawerQuery) {
      if (this.drawerQuery.removeEventListener) this.drawerQuery.removeEventListener('change', this.onDrawerQuery);
      else if (this.drawerQuery.removeListener) this.drawerQuery.removeListener(this.onDrawerQuery);
    }
  }

  toggle(id) { this.current === id ? this.close() : this.open(id); }

  open(id) {
    this.current = id;
    this.panels.forEach((p) => {
      p.dataset.open = String(p.dataset.mmPanel === id);
    });
    this.triggers.forEach((t) => {
      t.setAttribute('aria-expanded', String(t.dataset.mmTrigger === id));
    });
    this.moveMarker();
    this.syncVideos();
  }

  close() {
    this.current = null;
    this.panels.forEach((p) => { p.dataset.open = 'false'; });
    this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    this.moveMarker();
    this.syncVideos();
  }

  /* dev-build 663: step videos (How hiring works). A video plays only while
     its panel is open AND its step is the active one; every other video is
     paused, so nothing plays (or downloads, preload="none") out of sight.
     Only calls play()/pause() — never rewrites the DOM. */
  syncVideos() {
    this.querySelectorAll('video[data-mm-video]').forEach((v) => {
      const pane = v.closest('[data-mm-pane]');
      const panel = v.closest('[data-mm-panel]');
      const show = !!pane && pane.dataset.active === 'true' && !!panel && panel.dataset.open === 'true';
      if (show) {
        v.muted = true;
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      } else if (!v.paused) {
        v.pause();
      }
    });
  }

  /* ------------------------------------------------------------------------
     THE TRAVELLING MARKER — 11a, Ben's merge, approved 18-09-2026

     ONE accent marker for the whole bar rather than one per label. It follows
     the cursor while nothing is open, and parks under the open panel once
     there is one. The chevron nudge is pure CSS; only left/width need JS,
     because the labels are different widths and CSS cannot derive them.

     WHY IT MEASURES RATHER THAN USING A CSS TRICK: offsetLeft/offsetWidth are
     the real laid-out geometry, including the flex gap. There is no CSS way to
     say "as wide as the third sibling".

     IT RE-MEASURES ON FONT LOAD. The bar is Poppins; if the font arrives after
     first paint every label changes width and a marker positioned before that
     is wrong by a few pixels. document.fonts.ready is the only honest signal.
     ------------------------------------------------------------------------ */

  initMarker() {
    this.marker = this.querySelector('[data-mm-marker]');
    if (!this.marker) return;

    // Hover and focus both move it. Focus matters as much as hover: without it
    // the keyboard gets the bare unstyled bar that started this whole thread.
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('mouseenter', () => this.moveMarker(trigger));
      trigger.addEventListener('focus', () => this.moveMarker(trigger));
    });
    this.addEventListener('mouseleave', () => this.moveMarker());

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.moveMarker());
    }

    this.onMarkerResize = () => this.moveMarker();
    window.addEventListener('resize', this.onMarkerResize);
  }

  /* target: the trigger being pointed at, or nothing.
     An OPEN panel always wins over a hover — once a panel is open the marker
     belongs to it and stops previewing, which is what makes the open state
     feel settled rather than provisional. */
  moveMarker(target) {
    if (!this.marker || this.isDrawer) return;

    const openTrigger = this.current
      ? this.triggers.find((t) => t.dataset.mmTrigger === this.current)
      : null;
    const el = openTrigger || target;

    if (!el) {
      this.marker.dataset.visible = 'false';
      this.marker.dataset.open = 'false';
      return;
    }

    // The 10px inset matches the trigger's own 14px padding minus the optical
    // weight of the rule, so the marker sits under the WORD, not the hit area.
    this.marker.style.left = (el.offsetLeft + 10) + 'px';
    this.marker.style.width = Math.max(0, el.offsetWidth - 20) + 'px';
    this.marker.dataset.visible = 'true';
    this.marker.dataset.open = String(!!openTrigger);
  }

  /* ------------------------------------------------------------------------
     FRONT CHAT — the Live chat button, #170

     The chat id arrives as an attribute because this file is a static asset
     and cannot read a theme setting. No id means no init: the button keeps
     its ordinary href and nothing is loaded, so a missing id degrades to a
     link rather than to a dead button.

     THREE THINGS THE FRONT SDK GETS WRONG IF YOU ARE CASUAL WITH IT:
       1. init must be called ONCE per page load. The header renders on every
          page, so this guards with a window-level flag — a second init is not
          a no-op.
       2. A 'show' fired within about half a second of 'init' is silently
          dropped. onInitCompleted is the documented readiness signal, so a
          click before that is QUEUED and replayed, never discarded. Without
          the queue, the first click after a fresh page load does nothing —
          which is exactly the click a customer makes when they want help.
       3. useDefaultLauncher must be false, or Front paints its own bubble in
          the corner and the customer has two ways to start a chat.
     ------------------------------------------------------------------------ */

  initFrontChat() {
    const buttons = [...this.querySelectorAll('[data-mm-live-chat]')];
    const chatId = this.dataset.frontChatId;

    /* FIRST, before any guard — corrected 18-09-2026.

       In dev-build 110 this block sat BELOW the two configuration guards, so a
       missing checkbox or a missing ID returned before the object existed and
       the console answered "__djwChat is not defined". That told us the method
       had returned at a guard and nothing whatever about which one, i.e. it
       failed to report the only states it was added to report.

       A diagnostic that reports only when nothing is wrong is not a
       diagnostic. It is now the first thing the method does. */
    const state = window.__djwChat = window.__djwChat || {};
    state.buttonsFound = buttons.length;
    state.chatIdPresent = !!chatId;
    state.chatIdPreview = chatId ? chatId.slice(0, 6) + '…' : '(empty)';
    state.attributePresent = 'frontChatId' in this.dataset;
    state.stoppedAt = null;
    state.ready = false;

    /* REPORTED 18-09: the ID was entered and nothing happened.

       Two switches are needed and only one had been explained. The ID makes
       the widget available; the per-block checkbox says WHICH button opens
       it. With an ID and no ticked box there is nothing to bind, so this
       method used to return in silence — a correct no-op with an invisible
       cause, which is the failure mode this project keeps paying for.

       It now says which half is missing. console.info rather than warn: this
       is a configuration state, not a fault, and a warning in a merchant's
       console is noise they cannot act on. */
    if (!buttons.length) {
      state.stoppedAt = 'no button is flagged — the checkbox is not ticked on any block';
      if (chatId) {
        console.info(
          '[mega-menu] A Front chat ID is set but no button is flagged to open it. ' +
          'Tick "This button opens live chat" on the Help panel contact block, ' +
          'and on the Browse panel rail if that button should open it too.'
        );
      }
      return;
    }
    if (!chatId) {
      state.stoppedAt = state.attributePresent
        ? 'the Front chat ID setting is saved but empty'
        : 'the data-front-chat-id attribute is missing entirely — sections/header.liquid or the snippet is not the installed version';
      console.info(
        '[mega-menu] ' + buttons.length + ' button(s) are set to open live chat, but the ' +
        'Front chat ID is empty. Enter it in the Header section settings. ' +
        'Until then these buttons follow their own links.'
      );
      return;   // degrade to the link; load nothing
    }

    buttons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeDrawer();
        if (window.__djwFrontChatReady) {
          window.FrontChat('show');
        } else {
          window.__djwFrontChatWanted = true;   // replayed by onInitCompleted
        }
      });
    });

    /* ────────────────────────────────────────────────────────────────────
       INSTRUMENTED, because "still not working" twice is not something to
       answer with a third guess. Every step now records itself on
       window.__djwChat, so typing that one word in the browser console says
       exactly how far it got and where it stopped.

       The states it can report:
         buttons: 0        the checkbox is not ticked on any block
         chatId: ''        the Front chat ID is not saved
         scriptError: true the bundle did not load — blocked, offline, or the
                           URL is wrong. Ad blockers do block it.
         sdkMissing: true  the bundle loaded but window.FrontChat is absent,
                           which means it is not the file we think it is
         initError: '...'  Front rejected the init — almost always a chat ID
                           that is wrong, or a domain Front does not allow
         ready: true       Front says it is up. If a click still does nothing
                           after this, the fault is inside Front, not here.
       ──────────────────────────────────────────────────────────────────── */
    state.stoppedAt = 'nothing — both switches are set, loading the Front bundle';

    /* FRONT IS ALREADY ON THE PAGE. layout/theme.liquid line 249 loads
       chat.bundle.js itself and inits it with a hardcoded chatId and
       useDefaultLauncher: true. So this was loading the SDK a SECOND time
       and calling init again — and Front does not fire onInitCompleted for
       a second init, which is exactly why the 5s timeout below reported a
       failure while chat worked perfectly. My own instrumentation, wrong
       about my own code.

       Two loaders also means two launcher settings fighting: theme.liquid
       asks for Front's corner bubble, this asked for none.

       If FrontChat already exists, use it and load nothing. The buttons
       still work and there is one SDK on the page instead of two.

       TO DROP THE CORNER BUBBLE, if that is wanted: set useDefaultLauncher
       to false in theme.liquid. Not this file's call — that snippet
       predates it. */
    if (typeof window.FrontChat === 'function') {
      state.stoppedAt = 'nothing — Front was already loaded by theme.liquid';
      state.viaTheme = true;
      state.ready = true;
      window.__djwFrontChatReady = true;
      return;
    }

    if (window.__djwFrontChatStarted) {
      state.stoppedAt = 'nothing — another header instance already loaded it';
      return;
    }
    window.__djwFrontChatStarted = true;

    const script = document.createElement('script');
    script.src = 'https://chat-assets.frontapp.com/v1/chat.bundle.js';
    script.async = true;

    script.onerror = () => {
      state.scriptError = true;
      console.error(
        '[mega-menu] The Front chat bundle failed to load from ' + script.src + '. ' +
        'Most often an ad blocker or privacy extension. Check the Network tab, ' +
        'and try once in a private window with extensions off.'
      );
    };

    script.onload = () => {
      if (typeof window.FrontChat !== 'function') {
        state.sdkMissing = true;
        console.error(
          '[mega-menu] The Front bundle loaded but window.FrontChat is not defined. ' +
          'The script URL returned something that is not the Front SDK.'
        );
        return;
      }
      state.sdkLoaded = true;
      try {
        window.FrontChat('init', {
          chatId: chatId,
          useDefaultLauncher: false,
          onInitCompleted: () => {
            state.ready = true;
            window.__djwFrontChatReady = true;
            if (window.__djwFrontChatWanted) {
              window.__djwFrontChatWanted = false;
              window.FrontChat('show');
            }
          }
        });
      } catch (err) {
        state.initError = String(err && err.message || err);
        console.error('[mega-menu] Front rejected the init call: ', err);
        return;
      }

      /* Front does not reject a wrong chat ID — it accepts the call and then
         never calls onInitCompleted, so the button stays silently dead. Five
         seconds is long enough that a slow connection is not accused, and
         short enough to be in the console before anyone gives up. */
      setTimeout(() => {
        if (!state.ready) {
          state.initTimedOut = true;
          console.error(
            '[mega-menu] Front loaded but never finished initialising after 5s. ' +
            'This is what a wrong or inactive chat ID looks like — Front accepts ' +
            'the ID and then goes quiet. Re-copy it from Front › Settings › ' +
            'Channels › your Chat channel › Code snippet, and check the channel is on.'
          );
        }
      }, 5000);
    };

    document.head.appendChild(script);
  }

  /* ------------------------------------------------------------------------
     SEARCH PROBE — reported not working, 18-09-2026.

     Everything the theme's script needs is verified present by reading the
     files: <search-form> is defined in predictive-search.js, loaded globally
     and deferred from layout/theme.liquid; debounce() comes from app.js;
     theme.routes.predictive_search_url is rendered into window.theme;
     sections/predictive-search.liquid is what the fetch asks for. No CSS rule
     hides the container. So the fault is at runtime and reading more files
     will not find it.

     This observes rather than fixes. It does NOT re-implement the search —
     doing that would give two things listening to one input and make the real
     fault permanently invisible. Everything lands on window.__djwSearch.
     ------------------------------------------------------------------------ */

  probeSearch() {
    const shell = this.querySelector('[data-mm-search-probe]');
    if (!shell) return;

    const s = window.__djwSearch = {};
    s.shellFound = true;

    /* Did the custom element upgrade? An un-upgraded element is a plain
       HTMLElement, so connectedCallback never ran and nothing is bound. This
       is the single most likely cause and the one a screenshot cannot show. */
    s.elementDefined = !!(window.customElements && customElements.get('search-form'));
    s.upgraded = shell.constructor !== HTMLElement && shell.constructor.name !== 'HTMLElement';
    s.constructorName = shell.constructor.name;

    s.inputFound = !!shell.querySelector('.search-field');
    s.formFound = !!shell.querySelector('form');
    s.resultsContainerFound = !!shell.querySelector('.thb-predictive-search');
    s.clickCaptureFound = !!shell.querySelector('.searchform--click-capture');
    s.isDrawerFlag = shell.dataset.drawer || '(not set — correct for this one)';

    s.debounceDefined = typeof window.debounce === 'function';
    s.themeObject = typeof window.theme === 'object' && !!window.theme;
    s.predictiveUrl = (window.theme && window.theme.routes && window.theme.routes.predictive_search_url) || '(missing)';

    /* Another <search-form> already on the page is fine — the drawer has one —
       but worth counting, because the script binds .thb-quick-search globally
       and two instances both answer those buttons. */
    s.searchFormsOnPage = document.querySelectorAll('search-form').length;

    /* Watch the container. The script writes innerHTML and toggles .active,
       .loading and [inert], so a MutationObserver reports whether the script
       is doing anything at all without interfering with it. */
    const box = shell.querySelector('.thb-predictive-search');
    if (box) {
      s.observed = { classChanges: 0, contentWrites: 0, lastClasses: box.className, lastInert: box.hasAttribute('inert') };
      new MutationObserver((records) => {
        records.forEach((r) => {
          if (r.type === 'attributes') s.observed.classChanges++;
          if (r.type === 'childList') s.observed.contentWrites++;
        });
        s.observed.lastClasses = box.className;
        s.observed.lastInert = box.hasAttribute('inert');
        s.observed.computedDisplay = getComputedStyle(box).display;
      }).observe(box, { attributes: true, childList: true, attributeFilter: ['class', 'inert'] });
    }

    /* Count fetches to the predictive endpoint, so "did it even ask Shopify"
       is answerable. The original fetch is called through untouched. */
    if (!window.__djwFetchWrapped) {
      window.__djwFetchWrapped = true;
      const real = window.fetch;
      window.fetch = function (...args) {
        const url = String(args[0] || '');
        if (url.indexOf('predictive') > -1) {
          s.fetches = (s.fetches || 0) + 1;
          s.lastFetchUrl = url.slice(0, 120);
          return real.apply(this, args).then((res) => {
            s.lastFetchStatus = res.status;
            return res;
          }).catch((err) => {
            s.lastFetchError = String(err && err.message || err);
            throw err;
          });
        }
        return real.apply(this, args);
      };
    }

    /* THIS FIRED ON EVERY PAGE LOAD AND WAS ALWAYS WRONG.

       <body> opens at theme.liquid line 163 and predictive-search.js is
       loaded at line 209 — inside the body, AFTER the header. mega-menu.js
       is loaded from the header snippet, so it comes FIRST in document
       order, and deferred scripts execute in document order. The probe was
       asking whether search-form was defined at the one moment it could
       not be, then reporting a working feature as broken, in red, forever.

       whenDefined resolves when the element actually registers. Only the
       timeout is a real failure: "not yet" is normal, "never" is a fault. */
    if (!s.upgraded && window.customElements && customElements.whenDefined) {
      var settled = false;
      customElements.whenDefined('search-form').then(function () {
        settled = true;
        s.elementDefined = true;
        s.upgraded = true;
      });
      setTimeout(function () {
        if (settled) return;
        console.error(
          '[mega-menu] <search-form> never registered after 5s. ' +
          'predictive-search.js is loaded from layout/theme.liquid — check it is ' +
          'still there and that nothing above it threw. Typing does nothing until it is.'
        );
      }, 5000);
    }

    /* ONE THING THE PROBE CANNOT SEE, stated so it is not forgotten: whether
       Predictive search is switched ON in the theme settings. That gate is in
       Liquid, not in this script — search-drawer.liquid wraps its container in
       {% if settings.predictive_search_enabled %}. Our container is not
       wrapped, so ours renders either way, but the setting is still worth
       confirming before anything else is blamed. */
    console.info('[mega-menu] Search probe ready. Type in the field, then enter __djwSearch in this console.');
  }

  /* ------------------------------------------------------------------------
     THE CALL BUTTON — 12a, and #185 answered 20-09-2026

     WHAT WAS WRONG: this listened for 'djw:branch-change' and read
     window.DJW_BRANCH or data-djw-branch on <html>. Nothing in this theme
     dispatches that event or writes either global — all three were
     invented here rather than found. So the resolved state has been
     unreachable since build 114, every time.

     WHAT ALREADY EXISTS: booking-bar.js dispatches 'bookingbar:changed' on
     the document, from five paths, in THREE payload shapes:

       { locationGid, startDate, endDate }             a change
       { locationGid, startDate, endDate, fromCart }   page-load restore
       { cleared: true }                               booking cleared

     The third carries NO locationGid. Reading detail.locationGid blindly
     yields undefined there and would leave a stale branch showing after a
     customer cleared their booking, so 'cleared' is handled explicitly.

     THE GID NEEDS NO FETCH. window.locationMetaobjects is keyed by handle
     and carries gid and name for each, so the event's GID maps to the
     handle this markup uses with a plain lookup.

     THE INITIAL VALUE COMES FROM LIQUID — the snippet writes data-branch
     onto <mega-menu> from cart.attributes.Location. No fetch, no flash,
     right on first paint.

     WHY NOT REFETCH /cart.js ON THE EVENT: dispatchBookingBarChanged fires
     the event and THEN calls writeBookingAttributes. A listener that goes
     back to the cart reads the value from before the change. The detail is
     authoritative at event time; the cart is authoritative at page load.
     ------------------------------------------------------------------------ */

  initCall() {
    this.calls = [...this.querySelectorAll('[data-mm-call="root"]')];
    if (!this.calls.length) return;

    this.calls.forEach((call) => {
      const opener = call.querySelector('[data-mm-call-open]');
      if (opener) {
        opener.addEventListener('click', () => { call.dataset.open = 'true'; });
      }
    });

    this.applyBranch(this.serverBranch());

    this.onBookingChange = (e) => {
      const d = (e && e.detail) || {};
      if (d.cleared) { this.applyBranch(''); return; }
      this.applyBranch(this.handleFromGid(d.locationGid));
    };
    document.addEventListener('bookingbar:changed', this.onBookingChange);
  }

  /* Written by Liquid from the cart attribute. Empty when no booking. */
  serverBranch() {
    return (this.dataset.branch || '').trim().toLowerCase();
  }

  /* The event carries a GID; this markup carries a handle.
     locationMetaobjects is the bridge, and it is already on every page —
     the booking bar cannot run without it. */
  handleFromGid(gid) {
    if (!gid) return '';
    const bare = String(gid).replace('gid://shopify/Metaobject/', '');
    const all = window.locationMetaobjects || {};
    for (const handle in all) {
      const id = String((all[handle] || {}).gid || '').replace('gid://shopify/Metaobject/', '');
      if (id && id === bare) return handle.toLowerCase();
    }
    return '';
  }

  applyBranch(handle) {
    const want = (handle || '').trim().toLowerCase();
    this.calls.forEach((call) => {
      const halves = [...call.querySelectorAll('.mm-call__half')];
      const match = want
        ? halves.find((h) => (h.dataset.branch || '').toLowerCase() === want)
        : null;

      if (!match) {
        call.removeAttribute('data-resolved');
        halves.forEach((h) => h.removeAttribute('hidden'));
        call.dataset.open = 'false';
        return;
      }

      call.setAttribute('data-resolved', want);
      halves.forEach((h) => {
        if (h === match) h.removeAttribute('hidden');
        else h.setAttribute('hidden', '');
      });
    });
  }

  /* ---- mobile drawer mode ---- */

  get isDrawer() { return window.matchMedia('(max-width: 1279px)').matches; }

  openDrawer(trigger) {
    this.lastFocus = trigger || document.activeElement;
    this.dataset.drawer = 'open';
    this.dataset.level = 'root';
    if (this.overlay) this.overlay.dataset.open = 'true';
    document.documentElement.style.overflow = 'hidden';
    const close = this.querySelector('[data-mm-close]');
    if (close) close.focus();
  }

  closeDrawer() {
    this.dataset.drawer = 'closed';
    this.dataset.level = 'root';
    this.close();
    if (this.overlay) this.overlay.dataset.open = 'false';
    document.documentElement.style.overflow = '';
    if (this.lastFocus) this.lastFocus.focus();
  }

  /* Back goes from a panel to the trigger list, and returns focus to the row
     the customer came from — not to the top of the list. */
  back() {
    const id = this.current;
    this.dataset.level = 'root';
    this.close();
    const trigger = this.triggers.find((t) => t.dataset.mmTrigger === id);
    if (trigger) trigger.focus();
  }

  /* Steps inside the How Hiring Works panel. Panes are already stacked in one
     grid cell by CSS, so switching never changes the panel height.

     The rail selects on CLICK and arrow keys only, as of #175. It used to
     select on mouseenter as well; that was raised separately, exactly as the
     note here predicted, and removed. */
  initSteps(panel) {
    const buttons = [...panel.querySelectorAll('[data-mm-step]')];
    if (!buttons.length) return;
    const panes = [...panel.querySelectorAll('[data-mm-pane]')];

    const select = (key) => {
      buttons.forEach((b) => b.setAttribute('aria-selected', String(b.dataset.mmStep === key)));
      panes.forEach((p) => { p.dataset.active = String(p.dataset.mmPane === key); });
      this.syncVideos();
    };

    /* CLICK ONLY — #175, confirmed by Ben 18-09-2026.

       The mouseenter listener is deleted. It changed the pane under a cursor
       that was only passing through, so reading step three moved the content
       twice on the way there — the same complaint that killed hover-to-open,
       one level down.

       The click and arrow-key handlers were already here, so the rail now
       behaves like the tab strip it looks like. The panes are stacked in one
       grid cell, so the panel height does not move when one is chosen.

       What is lost: sweeping the cursor to preview all five steps. That is
       the behaviour being complained about. */
    buttons.forEach((button, i) => {
      button.addEventListener('click', () => select(button.dataset.mmStep));
      button.addEventListener('keydown', (e) => {
        const next = e.key === 'ArrowDown' ? i + 1 : e.key === 'ArrowUp' ? i - 1 : null;
        if (next === null) return;
        e.preventDefault();
        const target = buttons[(next + buttons.length) % buttons.length];
        target.focus();
        select(target.dataset.mmStep);
      });
    });

    select(buttons[0].dataset.mmStep);
  }
}

customElements.define('mega-menu', MegaMenu);

/* ==========================================================================
   Notes on the mobile mode

   There is no second component. Below 1280px the same <mega-menu> element is
   the drawer: data-level="root" shows the trigger list, data-level="panel"
   shows one panel. The methods above are the only mobile-specific code.

   Mobile always opened on tap, so below 1280px nothing about this change is
   visible. The click decision only alters desktop.
   ========================================================================== */
